import React, { useState, useEffect } from 'react';
import { X, Coins, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from './Toast';
import { formatCurrency } from '../utils/format';
import AppSelector from './AppSelector';
import { useHideNavOnModal } from '../hooks/useHideNavOnModal';

const ActivationModal = ({ show, onClose, preSelectedApp = null, apps = [], onSuccess,
                           resgate = false }) => {
  const { api, user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Resgate: pagar com o saldo de cashback em vez de créditos
  const [usarCashback, setUsarCashback] = useState(resgate);
  
  // Esconde nav quando modal está aberto
  useHideNavOnModal(show);
  const [newActivation, setNewActivation] = useState({
    app_uuid: preSelectedApp?.uuid || '',
    mac_address: '',
    email: '',
    code: '',
    key_app: ''
  });

  useEffect(() => {
    if (preSelectedApp) {
      setNewActivation(prev => ({ ...prev, app_uuid: preSelectedApp.uuid }));
    }
  }, [preSelectedApp]);

  useEffect(() => {
    if (show) setUsarCashback(resgate);
  }, [show, resgate]);

  const selectedApp = apps.find(a => a.uuid === newActivation.app_uuid) || preSelectedApp;
  const inputType = selectedApp?.input_type || 'mac_only';
  const appCost = selectedApp?.value || 0;
  const ehAdmin = user?.role === 'admin';

  // Cashback só vale para apps em destaque, que são os participantes do programa
  const appEmDestaque = !!(selectedApp?.is_featured ?? selectedApp?.featured);
  const saldoCashback = user?.saldo_cashback || 0;
  const podeUsarCashback = !ehAdmin && appEmDestaque && saldoCashback > 0;
  const pagaComCashback = podeUsarCashback && usarCashback;

  // Admin não paga
  const hasEnoughCredits = ehAdmin
    || (pagaComCashback ? (saldoCashback + (user?.credits || 0)) >= appCost
                        : (user?.credits || 0) >= appCost);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!hasEnoughCredits && !ehAdmin) {
      showToast.error('Saldo insuficiente!');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Preparar dados baseado no input_type - ENVIAR SÓ O NECESSÁRIO
      const activationData = {
        app_uuid: newActivation.app_uuid,
        usar_cashback: pagaComCashback
      };

      // Adicionar apenas os campos necessários baseado no input_type
      if (inputType === 'mac_only' || inputType === 'mac_key') {
        activationData.mac_address = newActivation.mac_address;
      }
      
      if (inputType === 'mac_key') {
        activationData.key_app = newActivation.key_app;
      }
      
      if (inputType === 'code') {
        activationData.code = newActivation.code;
      }
      
      if (inputType === 'email_password') {
        activationData.email = newActivation.email;
        activationData.key_app = newActivation.key_app; // password vai como key_app
      }
      
      await api.post('/api/activations', activationData);
      showToast.success('Ativação solicitada com sucesso!');
      
      // Reset
      setNewActivation({ app_uuid: '', mac_address: '', email: '', code: '', key_app: '' });
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Erro ao criar ativação');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatMacAddress = (value) => {
    // Remove tudo que não é número ou letra (permite A-Z, 0-9)
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, '');
    
    // Limita a 12 caracteres
    const limited = cleaned.substring(0, 12);
    
    // Adiciona ":" a cada 2 caracteres
    const formatted = limited.match(/.{1,2}/g)?.join(':') || limited;
    
    return formatted.toUpperCase();
  };

  const handleMacChange = (value) => {
    const formatted = formatMacAddress(value);
    setNewActivation({ ...newActivation, mac_address: formatted });
  };

  const handleClose = () => {
    setNewActivation({ app_uuid: '', mac_address: '', key_app: '' });
    onClose();
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={handleClose}
        >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 14, scale: 0.975 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 14, scale: 0.975 }}
        transition={{ duration: 0.22, ease: [0.2, 0.7, 0.3, 1] }}
        className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[88vh] overflow-y-auto my-auto"
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-xl font-bold dark:text-white">Nova Ativação</h2>
            <button onClick={handleClose}>
              <X size={24} className="text-gray-500 dark:text-gray-400" />
            </button>
          </div>
          
          {/* Credits Badge */}
          <div className="flex items-center justify-center gap-2 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-3 rounded-lg border-2 border-purple-200 dark:border-purple-700">
            <Coins className="text-purple-600" size={20} />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {user?.role === 'admin' ? 'Admin - Ativação Gratuita' : `Saldo: ${formatCurrency(user?.credits || 0)} créditos`}
            </span>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Show Selected App if preselected */}
          {preSelectedApp && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-lg border-2 border-blue-200 dark:border-blue-700">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">App Selecionado:</p>
              <div className="flex items-center gap-3">
                {preSelectedApp.logo_url ? (
                  <img 
                    src={preSelectedApp.logo_url} 
                    alt={preSelectedApp.name}
                    className="w-12 h-12 object-contain rounded-lg shadow-md"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center shadow-md">
                    <span className="text-white font-bold text-lg">
                      {preSelectedApp.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white">{preSelectedApp.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {user?.role === 'admin' ? 'GRATUITO' : `${preSelectedApp.value || 0} créditos`}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* App Selector */}
          {!preSelectedApp && (
            <div className="space-y-2">
              <label className="block text-sm font-medium dark:text-gray-200">
                Selecione o Aplicativo *
              </label>
              <AppSelector 
                apps={apps}
                selectedAppUuid={newActivation.app_uuid}
                onSelect={(uuid) => setNewActivation({ ...newActivation, app_uuid: uuid })}
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Campos dinâmicos baseado no input_type */}
          {newActivation.app_uuid && (
            <div className="space-y-4 animate-in fade-in slide-in-from-top-4 duration-300">
              <div className="h-px bg-gradient-to-r from-transparent via-purple-300 dark:via-purple-700 to-transparent"></div>
              
              {/* MAC Address - para mac_only e mac_key */}
              {(inputType === 'mac_only' || inputType === 'mac_key') && (
                <div>
                  <label className="block text-sm font-medium mb-2 dark:text-gray-200">MAC Address *</label>
                  <input
                    type="text"
                    value={newActivation.mac_address}
                    onChange={(e) => handleMacChange(e.target.value)}
                    className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="00:00:00:00:00:00"
                    maxLength="17"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {/* Key - para mac_key */}
              {inputType === 'mac_key' && (
                <div>
                  <label className="block text-sm font-medium mb-2 dark:text-gray-200">Key *</label>
                  <input
                    type="text"
                    value={newActivation.key_app}
                    onChange={(e) => setNewActivation({ ...newActivation, key_app: e.target.value })}
                    className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Digite a Key"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {/* Code - para code */}
              {inputType === 'code' && (
                <div>
                  <label className="block text-sm font-medium mb-2 dark:text-gray-200">Código *</label>
                  <input
                    type="text"
                    value={newActivation.code}
                    onChange={(e) => setNewActivation({ ...newActivation, code: e.target.value })}
                    className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Digite o código"
                    required
                    disabled={isSubmitting}
                  />
                </div>
              )}

              {/* Email e Senha - para email_password */}
              {inputType === 'email_password' && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-200">Email *</label>
                    <input
                      type="email"
                      value={newActivation.email}
                      onChange={(e) => setNewActivation({ ...newActivation, email: e.target.value })}
                      className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="seu@email.com"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 dark:text-gray-200">Senha *</label>
                    <input
                      type="password"
                      value={newActivation.key_app}
                      onChange={(e) => setNewActivation({ ...newActivation, key_app: e.target.value })}
                      className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Digite a senha"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                </>
              )}

              {/* Resgate: usar o saldo de cashback em vez dos créditos */}
              {podeUsarCashback && (
                <button
                  type="button"
                  onClick={() => setUsarCashback(v => !v)}
                  disabled={isSubmitting}
                  className={`w-full flex items-center gap-3 p-3.5 rounded-lg border-2 text-left transition-colors ${
                    usarCashback
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-emerald-400'
                  }`}
                >
                  <span className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border-2 ${
                    usarCashback ? 'bg-emerald-500 border-emerald-500' : 'border-gray-400'
                  }`}>
                    {usarCashback && <Check size={13} className="text-white" strokeWidth={3} />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
                      Pagar com cashback
                    </span>
                    <span className="block text-xs text-gray-600 dark:text-gray-400">
                      Saldo disponível: {formatCurrency(saldoCashback)}
                      {saldoCashback < appCost && ' — o restante sai dos créditos'}
                    </span>
                  </span>
                </button>
              )}

              {/* Cost Display */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-lg border-2 border-purple-200 dark:border-purple-700">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Custo:</span>
                  <span className="text-lg font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                    {ehAdmin
                      ? 'GRATUITO'
                      : `${formatCurrency(appCost)} ${pagaComCashback ? 'em cashback' : 'créditos'}`}
                  </span>
                </div>
                {!ehAdmin && appEmDestaque && !pagaComCashback && (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-2 font-medium">
                    Este app rende cashback ao ser ativado com créditos
                  </p>
                )}
                {!hasEnoughCredits && !ehAdmin && (
                  <p className="text-xs text-red-600 mt-2 font-medium">⚠️ Saldo insuficiente!</p>
                )}
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!newActivation.app_uuid || isSubmitting || (!hasEnoughCredits && user?.role !== 'admin')}
            className={`w-full py-4 rounded-xl font-bold text-white transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${
              !newActivation.app_uuid || isSubmitting || (!hasEnoughCredits && user?.role !== 'admin')
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
            }`}
          >
            {isSubmitting ? 'Processando...' : 'Ativar Aplicativo'}
          </button>
        </form>
      </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ActivationModal;
